import React, { useState } from "react";
import styled from "@emotion/styled";
import { Typography, Box, Card, Divider } from "@mui/material";
import HoverTooltip from "./HoverTooltip";
import { KeyboardArrowUp, KeyboardArrowDown, RunCircle, WarningAmber, AccessTime } from "@mui/icons-material";
import TaskIcon from "@mui/icons-material/Task";
import CommentIcon from "@mui/icons-material/Comment";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";
import UserMenuChip from "./UserMenuChip";
import UserAssignmentDialog from "./UserAssignmentDialog";
import { useHasPermission } from "../utils/permissionUtils";
import { useIsLocked } from "../utils/lockUtils";
import { addUpTime, convertMinutesToHours } from "../utils/timeUtils";
import { formatDate } from "../utils/dateUtils";

const ProjectCardContainer = styled(Card)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "space-between",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    boxShadow: theme.shadows[3],
    border: `1px solid ${theme.palette.divider}`,
    "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: theme.shadows[8],
        borderColor: theme.palette.primary.main,
    },
    [theme.breakpoints.up("sm")]: {
        maxWidth: theme.breakpoints.values.sm,
    },
}));

const TitleRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(0.5),
    flexGrow: 1,
}));

const ContentBox = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(0.5),
}));

const ProjectName = styled(Typography)(({ theme }) => ({
    fontWeight: "bold",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    cursor: "pointer",
    textDecoration: "none",
    color: "inherit",
    "&:hover": {
        color: theme.palette.primary.dark,
    },
}));

const ProjectNumber = styled(Typography)(({ theme }) => ({
    display: "block",
    textAlign: "right",
    fontFamily: "monospace",
    fontSize: theme.typography.size.xs,
    color: theme.palette.text.disabled,
    lineHeight: 1.4,
}));

const ProjectDetails = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(0.5),
}));

const StatusIcons = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    flexShrink: 0,
    gap: theme.spacing(0.25),
}));

const IconRow = styled("div")(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(2),
}));

const IconGroup = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
}));

const IconItem = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: "4px",
}));

const BudgetBox = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    padding: theme.spacing(0.5, 1),
    minWidth: theme.spacing(10),
}));

const SectionDivider = styled(Divider)(({ theme }) => ({
    margin: theme.spacing(1, 0),
}));

const TimeTrackingHeader = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(0.75),
}));

const TimeTrackingColumns = styled(Box)({
    display: "flex",
    justifyContent: "space-around",
    textAlign: "center",
});

const DoubleArrowUp = styled(KeyboardArrowUp)(({ theme }) => ({
    marginBottom: theme.spacing(-2.25),
    marginTop: theme.spacing(-0.5),
}));

const DoubleArrowDown = styled(KeyboardArrowDown)(({ theme }) => ({
    marginBottom: theme.spacing(-2.25),
    marginTop: theme.spacing(-0.5),
}));

const PriorityCell = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(0),
    gap: theme.spacing(0),
}));

const getPriorityIcon = (priority) => {
    switch (priority) {
        case "Highest":
            return (
                <HoverTooltip title="Highest Priority">
                    <PriorityCell>
                        <DoubleArrowUp color="error" />
                        <KeyboardArrowUp color="error" />
                    </PriorityCell>
                </HoverTooltip>
            );
        case "High":
            return (
                <HoverTooltip title="High Priority">
                    <KeyboardArrowUp color="warning" />
                </HoverTooltip>
            );
        case "Low":
            return (
                <HoverTooltip title="Low Priority">
                    <KeyboardArrowDown color="primary" />
                </HoverTooltip>
            );
        case "Lowest":
            return (
                <HoverTooltip title="Lowest Priority">
                    <PriorityCell>
                        <DoubleArrowDown color="primary" />
                        <KeyboardArrowDown color="primary" />
                    </PriorityCell>
                </HoverTooltip>
            );
        default:
            return null;
    }
};

const AccountTabProjects = ({ project }) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const hasPermission = useHasPermission();
    const canAssignProject = hasPermission("assignProject");
    const canSeeInternal = hasPermission("canSeeInternal");
    const isLocked = useIsLocked(project?.account, project?.account?.client);

    const handleOpenDialog = () => {
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
    };

    const actualHours = addUpTime(convertMinutesToHours(project.time?.total)).toFixed(2);

    return (
        <ProjectCardContainer>
            {project.projectId && <ProjectNumber variant="caption">Project #{project.projectId}</ProjectNumber>}
            <TitleRow>
                <StatusIcons>
                    {project.rush && (
                        <HoverTooltip title="Rush">
                            <RunCircle color="error" fontSize="small" />
                        </HoverTooltip>
                    )}
                    {getPriorityIcon(project.priority)}
                    {project.remainingDays === "Overdue" && (
                        <HoverTooltip title="Project is Overdue">
                            <WarningAmber color="warning" fontSize="small" />
                        </HoverTooltip>
                    )}
                </StatusIcons>
                <HoverTooltip title={project.name} arrow>
                    <ProjectName variant="body2" component={Link} to={`/account/${project.account.slug}/project/${project._id}`}>
                        {project.name}
                    </ProjectName>
                </HoverTooltip>
            </TitleRow>

            <ContentBox>
                <ProjectDetails>
                    <Typography variant="body2" color="text.secondary">Start Date:</Typography>
                    <Typography variant="body2">{formatDate(project.startDate) || <Skeleton variant="text" />}</Typography>
                </ProjectDetails>
                <ProjectDetails>
                    <Typography variant="body2" color="text.secondary">Due Date:</Typography>
                    <Typography variant="body2">{formatDate(project.dueDate) || <Skeleton variant="text" />}</Typography>
                </ProjectDetails>
                {project.completedDate && (
                    <ProjectDetails>
                        <Typography variant="body2" color="text.secondary">Completed Date:</Typography>
                        <Typography variant="body2">{formatDate(project.completedDate) || <Skeleton variant="text" />}</Typography>
                    </ProjectDetails>
                )}
                {!project.completedDate && (
                    <ProjectDetails>
                        <Typography variant="body2" color="text.secondary">Remaining Days:</Typography>
                        <Typography variant="body2" color={project.remainingDays === "Overdue" ? "error" : "text.primary"}>
                            {project.remainingDays || <Skeleton variant="text" />}
                        </Typography>
                    </ProjectDetails>
                )}
                <ProjectDetails>
                    <Typography variant="body2" color="text.secondary">Expected Duration:</Typography>
                    <Typography variant="body2">{project.expectedDuration + " Days" || <Skeleton variant="text" />}</Typography>
                </ProjectDetails>
                {project.completedDate && (
                    <ProjectDetails>
                        <Typography variant="body2" color="text.secondary">Actual Duration:</Typography>
                        <Typography variant="body2">{project.actualDuration + " Days" || <Skeleton variant="text" />}</Typography>
                    </ProjectDetails>
                )}

                <SectionDivider />
                <TimeTrackingHeader>
                    <AccessTime fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">Time Tracking</Typography>
                </TimeTrackingHeader>
                <TimeTrackingColumns>
                    {canSeeInternal && (
                        <BudgetBox>
                            <Typography variant="caption" color="text.secondary" display="block">Budgeted</Typography>
                            <Typography variant="body2">{(project.budget?.budgeted || 0).toFixed(2)}</Typography>
                        </BudgetBox>
                    )}
                    <BudgetBox>
                        <Typography variant="caption" color="text.secondary" display="block">Quoted</Typography>
                        <Typography variant="body2">{(project.budget?.quoted || 0).toFixed(2)}</Typography>
                    </BudgetBox>
                    <BudgetBox>
                        <Typography variant="caption" color="text.secondary" display="block">Actual</Typography>
                        <Typography variant="body2" color={actualHours > (project.budget?.quoted || 0) ? "error" : "success.main"}>{actualHours}</Typography>
                    </BudgetBox>
                </TimeTrackingColumns>

                <IconRow>
                    <IconGroup>
                        <UserMenuChip user={project.responsible} size="small" onReAssign={canAssignProject && !isLocked ? handleOpenDialog : undefined} canSeeUnassigned={canAssignProject && !isLocked} menuItems={{ reAssign: canAssignProject && !isLocked }} />
                    </IconGroup>
                    <IconGroup>
                        <IconItem>
                            <TaskIcon fontSize="small" />
                            <Typography variant="caption">{project.taskNumber}</Typography>
                        </IconItem>
                        <IconItem>
                            <CommentIcon fontSize="small" />
                            <Typography variant="caption">{project.commentNumber}</Typography>
                        </IconItem>
                    </IconGroup>
                </IconRow>
            </ContentBox>

            <UserAssignmentDialog
                isOpen={isDialogOpen}
                onClose={handleCloseDialog}
                objectId={project._id}
                objectType="project"
                accountId={project.account?._id || project.account}
                singleSelection={true}
            />
        </ProjectCardContainer>
    );
};

export default AccountTabProjects;
